import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiTruck,
  FiX,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const number = (value) => Number(value || 0);

const quantity = (value) =>
  number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });

const date = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const statusStyles = {
  DRAFT: "bg-gray-100 text-gray-700",
  DISPATCHED: "bg-blue-100 text-blue-700",
  PARTIALLY_RECEIVED: "bg-orange-100 text-orange-700",
  RECEIVED: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const statusLabel = (status) =>
  (status || "").replaceAll("_", " ");

export default function StockTransferManagementPage() {
  const navigate = useNavigate();
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [transfers, setTransfers] = useState([]);
  const [accessibleStoreIds, setAccessibleStoreIds] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("ALL");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [receivingTransfer, setReceivingTransfer] = useState(null);

  const loadData = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const [transfersData, storesData] = await Promise.all([
        apiRequest("/inventory/transfers", { accessToken }),
        apiRequest("/inventory/stores", { accessToken }),
      ]);

      setTransfers(transfersData || []);
      setAccessibleStoreIds((storesData || []).map((store) => store.id));
    } catch (err) {
      setError(err.message || "Unable to load stock transfers.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadData();
  }, [isAuthLoading, accessToken, loadData]);

  const filteredTransfers = useMemo(() => {
    const term = search.trim().toLowerCase();

    return transfers.filter((transfer) => {
      const matchesSearch =
        !term ||
        transfer.transferNumber?.toLowerCase().includes(term) ||
        transfer.sourceStore?.name?.toLowerCase().includes(term) ||
        transfer.destinationStore?.name?.toLowerCase().includes(term) ||
        transfer.items?.some(
          (item) =>
            item.variant?.product?.name?.toLowerCase().includes(term) ||
            item.variant?.sku?.toLowerCase().includes(term),
        );

      const isOutgoing = accessibleStoreIds.includes(
        transfer.sourceStoreId,
      );

      const isIncoming = accessibleStoreIds.includes(
        transfer.destinationStoreId,
      );

      return (
        matchesSearch &&
        (!status || transfer.status === status) &&
        (tab === "ALL" ||
          (tab === "OUTGOING" && isOutgoing) ||
          (tab === "INCOMING" && isIncoming))
      );
    });
  }, [accessibleStoreIds, search, status, tab, transfers]);

  const summary = useMemo(
    () => ({
      drafts: transfers.filter((item) => item.status === "DRAFT").length,
      dispatched: transfers.filter((item) =>
        ["DISPATCHED", "PARTIALLY_RECEIVED"].includes(item.status),
      ).length,
      received: transfers.filter((item) => item.status === "RECEIVED").length,
      unitsInTransit: transfers
        .filter((item) =>
          ["DISPATCHED", "PARTIALLY_RECEIVED"].includes(item.status),
        )
        .reduce(
          (total, transfer) =>
            total +
            transfer.items.reduce(
              (itemTotal, item) =>
                itemTotal +
                (number(item.quantityDispatched) -
                  number(item.quantityReceived)),
              0,
            ),
          0,
        ),
    }),
    [transfers],
  );

  const dispatchTransfer = async (transfer) => {
    try {
      setActionLoading(transfer.id);
      setError("");
      setSuccess("");

      const response = await apiRequest(
        `/inventory/transfers/${transfer.id}/dispatch`,
        {
          method: "POST",
          accessToken,
        },
      );

      setSuccess(`${response.transferNumber} dispatched successfully.`);
      await loadData();
    } catch (err) {
      setError(err.message || "Unable to dispatch transfer.");
    } finally {
      setActionLoading("");
    }
  };

  const cancelTransfer = async (transfer) => {
    const confirmed = window.confirm(
      `Cancel draft transfer ${transfer.transferNumber}?`,
    );

    if (!confirmed) return;

    try {
      setActionLoading(transfer.id);
      setError("");
      setSuccess("");

      await apiRequest(`/inventory/transfers/${transfer.id}/cancel`, {
        method: "POST",
        accessToken,
      });

      setSuccess(`${transfer.transferNumber} cancelled.`);
      await loadData();
    } catch (err) {
      setError(err.message || "Unable to cancel transfer.");
    } finally {
      setActionLoading("");
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">
            Inventory / Stock Transfers
          </p>

          <h1 className="mt-1 text-3xl font-bold">
            Transfer Management
          </h1>

          <p className="mt-2 text-gray-500">
            Dispatch outgoing transfers and receive stock sent to your branch.
          </p>
        </div>

        <button
          onClick={() =>
            navigate("/admindashboard/inventory/transfers/create")
          }
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white"
        >
          <FiTruck />
          New Transfer
        </button>
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <StatCard title="Draft Transfers" value={summary.drafts} />
        <StatCard
          title="Awaiting Receipt"
          value={summary.dispatched}
          color="text-orange-600"
        />
        <StatCard
          title="Completed"
          value={summary.received}
          color="text-green-600"
        />
        <StatCard
          title="Units in Transit"
          value={quantity(summary.unitsInTransit)}
          color="text-primary-700"
        />
      </div>

      {error && (
        <div className="mb-6 rounded-2xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-2xl bg-green-50 p-4 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-5">
          <div className="mb-4 flex flex-wrap gap-2">
            {[
              ["ALL", "All Transfers"],
              ["OUTGOING", "Outgoing"],
              ["INCOMING", "Incoming"],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setTab(value)}
                className={`rounded-xl px-4 py-2 text-sm font-medium ${
                  tab === value
                    ? "bg-primary-700 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-4">
            <div className="relative lg:col-span-2">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search transfer, store, product, SKU..."
                className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
              />
            </div>

            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="h-12 rounded-xl border border-gray-200 px-4"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="DISPATCHED">Dispatched</option>
              <option value="PARTIALLY_RECEIVED">
                Partially Received
              </option>
              <option value="RECEIVED">Received</option>
              <option value="CANCELLED">Cancelled</option>
            </select>

            <button
              onClick={loadData}
              className="flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 hover:bg-gray-50"
            >
              <FiRefreshCw />
              Refresh
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {loading ? (
            <p className="p-10 text-center text-gray-500">
              Loading transfers…
            </p>
          ) : filteredTransfers.length === 0 ? (
            <p className="p-10 text-center text-gray-500">
              No transfers found.
            </p>
          ) : (
            filteredTransfers.map((transfer) => (
              <TransferRow
                key={transfer.id}
                transfer={transfer}
                canDispatch={accessibleStoreIds.includes(
                  transfer.sourceStoreId,
                )}
                canReceive={accessibleStoreIds.includes(
                  transfer.destinationStoreId,
                )}
                loading={actionLoading === transfer.id}
                onDispatch={() => dispatchTransfer(transfer)}
                onCancel={() => cancelTransfer(transfer)}
                onReceive={() => setReceivingTransfer(transfer)}
              />
            ))
          )}
        </div>
      </div>

      {receivingTransfer && (
        <ReceiveTransferModal
          transfer={receivingTransfer}
          onClose={() => setReceivingTransfer(null)}
          onReceived={async (message) => {
            setReceivingTransfer(null);
            setSuccess(message);
            await loadData();
          }}
        />
      )}
    </div>
  );
}

function TransferRow({
  transfer,
  canDispatch,
  canReceive,
  loading,
  onDispatch,
  onCancel,
  onReceive,
}) {
  const [open, setOpen] = useState(false);

  const totalRequested = transfer.items.reduce(
    (total, item) => total + number(item.quantityRequested),
    0,
  );

  const totalReceived = transfer.items.reduce(
    (total, item) => total + number(item.quantityReceived),
    0,
  );

  return (
    <div className="p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <button
            onClick={() => setOpen((value) => !value)}
            className="mt-1 rounded-lg p-2 text-gray-500 hover:bg-gray-100"
          >
            {open ? <FiChevronUp /> : <FiChevronDown />}
          </button>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-gray-900">
                {transfer.transferNumber}
              </h3>

              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  statusStyles[transfer.status]
                }`}
              >
                {statusLabel(transfer.status)}
              </span>
            </div>

            <p className="mt-2 text-sm text-gray-500">
              {transfer.sourceStore?.name} → {transfer.destinationStore?.name}
            </p>

            <p className="mt-1 text-xs text-gray-400">
              Created {date(transfer.createdAt)} · {transfer.items.length} item
              {transfer.items.length === 1 ? "" : "s"} · Requested:{" "}
              {quantity(totalRequested)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {transfer.status === "DRAFT" && canDispatch && (
            <>
              <button
                disabled={loading}
                onClick={onCancel}
                className="rounded-xl border border-red-200 px-4 py-2 text-sm text-red-600 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                disabled={loading}
                onClick={onDispatch}
                className="flex items-center gap-2 rounded-xl bg-primary-700 px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                <FiTruck />
                Dispatch
              </button>
            </>
          )}

          {["DISPATCHED", "PARTIALLY_RECEIVED"].includes(
            transfer.status,
          ) &&
            canReceive && (
              <button
                disabled={loading}
                onClick={onReceive}
                className="flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                <FiCheckCircle />
                Receive Stock
              </button>
            )}
        </div>
      </div>

      {open && (
        <div className="mt-5 overflow-x-auto rounded-2xl bg-gray-50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">Product Variant</th>
                <th className="px-4 py-3 text-right">Requested</th>
                <th className="px-4 py-3 text-right">Dispatched</th>
                <th className="px-4 py-3 text-right">Received</th>
                <th className="px-4 py-3">Notes</th>
              </tr>
            </thead>

            <tbody>
              {transfer.items.map((item) => (
                <tr key={item.id} className="border-b border-gray-100">
                  <td className="px-4 py-3">
                    <p className="font-medium">
                      {item.variant?.product?.name}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {item.variant?.sku}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {quantity(item.quantityRequested)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {quantity(item.quantityDispatched)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {quantity(item.quantityReceived)}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {item.notes || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReceiveTransferModal({ transfer, onClose, onReceived }) {
  const { accessToken } = useAuth();

  const [items, setItems] = useState(
    transfer.items
      .map((item) => ({
        variantId: item.variantId,
        quantityReceived:
          number(item.quantityDispatched) - number(item.quantityReceived),
        notes: "",
      }))
      .filter((item) => item.quantityReceived > 0),
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const updateItem = (index, field, value) => {
    setItems((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const submitReceipt = async () => {
    if (
      items.some(
        (item) =>
          item.quantityReceived === "" ||
          number(item.quantityReceived) <= 0,
      )
    ) {
      setError("Enter a received quantity greater than zero for each row.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const response = await apiRequest(
        `/inventory/transfers/${transfer.id}/receive`,
        {
          method: "POST",
          accessToken,
          body: {
            items: items.map((item) => ({
              variantId: item.variantId,
              quantityReceived: number(item.quantityReceived),
              notes: item.notes.trim() || undefined,
            })),
          },
        },
      );

      onReceived(
        `${response.transferNumber} received successfully. Status: ${statusLabel(
          response.status,
        )}.`,
      );
    } catch (err) {
      setError(err.message || "Unable to receive transfer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-2xl font-bold">Receive Transfer</h2>
            <p className="mt-1 text-sm text-gray-500">
              {transfer.transferNumber} · From {transfer.sourceStore?.name}
            </p>
          </div>

          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100"
          >
            <FiX />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto p-6">
          {error && (
            <div className="mb-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {items.map((receivedItem, index) => {
              const transferItem = transfer.items.find(
                (item) => item.variantId === receivedItem.variantId,
              );

              const remaining =
                number(transferItem?.quantityDispatched) -
                number(transferItem?.quantityReceived);

              return (
                <div
                  key={receivedItem.variantId}
                  className="rounded-2xl border border-gray-200 p-4"
                >
                  <p className="font-semibold">
                    {transferItem?.variant?.product?.name}
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    {transferItem?.variant?.sku} · Remaining:{" "}
                    {quantity(remaining)}
                  </p>

                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Quantity Received
                      </label>

                      <input
                        type="number"
                        min="0.001"
                        max={remaining}
                        step="0.001"
                        value={receivedItem.quantityReceived}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "quantityReceived",
                            event.target.value,
                          )
                        }
                        className="h-11 w-full rounded-xl border border-gray-200 px-4"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Receipt Note
                      </label>

                      <input
                        value={receivedItem.notes}
                        onChange={(event) =>
                          updateItem(index, "notes", event.target.value)
                        }
                        placeholder="Optional discrepancy note"
                        className="h-11 w-full rounded-xl border border-gray-200 px-4"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-5">
          <button
            onClick={onClose}
            className="h-11 rounded-xl border border-gray-200 px-5"
          >
            Cancel
          </button>

          <button
            disabled={saving}
            onClick={submitReceipt}
            className="flex h-11 items-center gap-2 rounded-xl bg-green-600 px-5 text-white disabled:opacity-60"
          >
            <FiCheckCircle />
            {saving ? "Receiving…" : "Confirm Receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, color = "text-gray-900" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className={`mt-2 text-3xl font-bold ${color}`}>{value}</h3>
    </div>
  );
}